import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input, Modal, notification, Spin, Typography } from "antd";
import { observer } from "mobx-react-lite";

import { contactWebgisOwner, getWebgisInfo } from "../../api/webgis";
import { userStore } from "../../stores/userStore";
import { usePendingAction } from "../../utils/usePendingAction";

interface ContactOwnerModalProps {
  open: boolean;
  onClose: () => void;
  webgisGuid: string;
}

export const ContactOwnerModal = observer(
  ({ open, onClose, webgisGuid }: ContactOwnerModalProps) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [fetchError, setFetchError] = useState(false);
    const [webgisUrl, setWebgisUrl] = useState<string | null>(null);
    const [message, setMessage] = useState("");
    const [messageError, setMessageError] = useState(false);
    const [sent, setSent] = useState(false);
    const { pending: sending, run: runSend } = usePendingAction();

    useEffect(() => {
      if (open && webgisGuid) {
        setLoading(true);
        setFetchError(false);
        setWebgisUrl(null);
        getWebgisInfo(webgisGuid)
          .then((info) => {
            setWebgisUrl(info.url);
          })
          .catch(() => {
            setFetchError(true);
          })
          .finally(() => {
            setLoading(false);
          });
      }
    }, [open, webgisGuid]);

    const handleClose = () => {
      setMessage("");
      setMessageError(false);
      setSent(false);
      setWebgisUrl(null);
      setFetchError(false);
      onClose();
    };

    const handleRequestClose = () => {
      if (sending) return;
      if (!sent && !fetchError && !loading && message.trim()) {
        Modal.confirm({
          content: t("contactOwner.closeConfirmation"),
          onOk: handleClose,
          okText: t("app.close"),
          cancelText: t("app.cancel"),
        });
      } else {
        handleClose();
      }
    };

    const handleSend = async () => {
      if (!message.trim()) {
        setMessageError(true);
        return;
      }
      await runSend(async () => {
        try {
          await contactWebgisOwner({ instanceGuid: webgisGuid, message });
          setSent(true);
        } catch {
          notification.error({ message: t("contactOwner.sendError") });
        }
      });
    };

    const displayUrl = webgisUrl
      ? webgisUrl.replace(/^https?:\/\//, "")
      : webgisGuid;

    const title =
      loading || fetchError || sent
        ? null
        : t("contactOwner.title", { webgisUrl: displayUrl });

    const footer = loading
      ? null
      : fetchError || sent
        ? [
            <Button key="close" type="primary" onClick={handleClose}>
              {t("app.close")}
            </Button>,
          ]
        : [
            <Button
              key="send"
              type="primary"
              loading={sending}
              onClick={handleSend}
            >
              {t("contactOwner.send")}
            </Button>,
          ];

    return (
      <Modal
        open={open}
        onCancel={handleRequestClose}
        title={title}
        centered
        width={520}
        closable={!sending}
        keyboard={!sending}
        mask={{ closable: !sending }}
        footer={footer}
      >
        {loading && (
          <div style={{ textAlign: "center", padding: "32px 0" }}>
            <Spin />
          </div>
        )}

        {!loading && fetchError && (
          <Typography.Text>{t("contactOwner.invalidGuid")}</Typography.Text>
        )}

        {!loading && !fetchError && sent && (
          <>
            <Typography.Title level={4} style={{ marginTop: 0 }}>
              {t("contactOwner.sentTitle")}
            </Typography.Title>
            <Typography.Text>
              {t("contactOwner.sentDescription")}
            </Typography.Text>
          </>
        )}

        {!loading && !fetchError && !sent && (
          <>
            <Typography.Text strong>
              {t("contactOwner.emailLabel")}
            </Typography.Text>
            <Input
              value={userStore.profile?.email ?? ""}
              disabled
              style={{ marginTop: 4, marginBottom: 4 }}
            />
            <Typography.Text
              type="secondary"
              style={{ display: "block", marginBottom: 16 }}
            >
              {t("contactOwner.emailHint")}
            </Typography.Text>
            <Typography.Text strong>
              {t("contactOwner.messageLabel")}
            </Typography.Text>
            <Input.TextArea
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                setMessageError(false);
              }}
              rows={6}
              status={messageError ? "error" : undefined}
              style={{ marginTop: 4 }}
            />
            {messageError && (
              <Typography.Text
                type="danger"
                style={{ display: "block", marginTop: 4 }}
              >
                {t("contactOwner.messageRequired")}
              </Typography.Text>
            )}
          </>
        )}
      </Modal>
    );
  },
);
